import { Button } from '@platform/ui-library';
import {
  FormEvent,
  useEffect,
  useRef,
  useState
} from 'react';
import { Input, InputFieldType, Message, MessageStatus } from '@app/form_components';
import { useServices } from '@app/hooks';
import { LoginState, LoginPasswordState } from './login-state';
import { mfaErrorMessage } from './mfaErrorMessage';

export type LoginPasswordFormProps = {
  onComplete: () => void;
  state: LoginPasswordState;
  setState: (values: LoginState) => void;
};

export function LoginPasswordForm({
  onComplete,
  state,
  setState
}: LoginPasswordFormProps) {
  const { userService } = useServices();
  const contentRef = useRef<HTMLFormElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { password, username } = state;

  useEffect(() => {
    requestAnimationFrame(() => {
      contentRef.current?.querySelector('input')?.focus();
    });
  }, []);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!password || !username) {
      setErrorMessage('Enter username & password before proceeding.');
    }

    setIsLoading(true);
    try {
      const settings = await userService.getAuthenticationSettings(state);
      if (!settings.validUsernameAndPassword) {
        setErrorMessage('Invalid username or password.');
        return;
      }

      if (settings.isMfaEnabled && settings.isRemembered) {
        await userService.login({
          ...state,
          ...settings
        });
        onComplete();
      } else {
        setState({
          code: '',
          ...state,
          ...settings
        });
      }

      setIsLoading(false);
    } catch (error) {
      setErrorMessage(await mfaErrorMessage(error));
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} ref={contentRef}>
      <div className="box header">
        <h2>Login</h2>
      </div>
      <div className="box content">
        <Input
          label="Username"
          name="username"
          onChange={(username) => {
            setState({ ...state, username });
          }}
          placeholder="User ID"
          required
          value={username}
        />
        <Input
          label="Password"
          name="password"
          onChange={(password) => {
            setState({ ...state, password });
          }}
          placeholder="*******"
          required
          type={InputFieldType.password}
          value={password}
        />
        <Message
          className="spacePreWrap"
          message={errorMessage}
          status={MessageStatus.error}
        />
      </div>
      <div className="box footer no-border">
        <Button type="submit" theme='fancy'>
          {(isLoading) ? 'Loading...' : 'Next'}
        </Button>
      </div>
    </form>
  );
}
